import {
  AssetLocation,
  AssetResponseDto,
  AssetStatus,
  AssetType,
  LocationTypeName,
  TrayDetails,
  TrayType,
} from '@peritasai/types';

/**
 * Asset entity representing the core business object
 * This entity contains business logic and validation
 */
export class AssetEntity {
  public readonly id: string;
  public readonly name: string;
  public readonly description: string | null;
  public readonly type: AssetType;
  public readonly status: AssetStatus | null;
  public readonly manufacturer: string;
  public readonly referenceNumber: string;
  public readonly createdAt: Date;
  public readonly assetCatalogId: string;
  public readonly locationUpdatedAt: Date | null;
  public readonly trayDetails: TrayDetails | null;
  public readonly location: unknown | null;
  public readonly locationDisplayName: string | null;
  public readonly caseId: string | null;

  constructor(dto: AssetResponseDto) {
    this.id = dto.id;
    this.name = dto.name;
    this.description = dto.description ?? null;
    this.type = dto.type;
    this.status = dto.status;
    this.manufacturer = dto.manufacturer;
    this.referenceNumber = dto.referenceNumber;
    this.createdAt = new Date(dto.createdAt); // Ensure proper Date object
    this.assetCatalogId = dto.assetCatalogId;
    this.locationUpdatedAt = dto.locationUpdatedAt
      ? new Date(dto.locationUpdatedAt)
      : null;
    this.trayDetails = dto.trayDetails as TrayDetails | null;
    this.location = dto.location;
    this.locationDisplayName = dto.locationDisplayName ?? null;
    this.caseId = dto.caseId ?? null;
  }

  /**
   * Get formatted creation date string
   */
  public getFormattedCreatedAt(): string {
    return this.createdAt.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  /**
   * Get formatted creation date and time string
   */
  public getFormattedCreatedAtDateTime(): string {
    return this.createdAt.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Get location display value
   * Returns 'N/A' if location is null
   */
  public getLocationDisplay(): string {
    if (!this.location) {
      return 'N/A';
    }
    if (this.locationDisplayName) {
      return this.locationDisplayName.toLocaleUpperCase();
    }
    // Try to get location name or id from location object
    const locationObj = this.location as AssetLocation & {
      displayLocation?: string;
    };
    const locationName =
      locationObj.displayLocation ||
      locationObj.name ||
      locationObj.id ||
      'N/A';
    let display = locationName;
    if (locationName !== `${LocationTypeName.UNKNOWN}`) {
      const sublocation = locationObj.departmentOrUnit || locationObj.floor;
      display = sublocation ? `${locationName} - ${sublocation}` : locationName;
    }
    return display.toLocaleUpperCase();
  }

  /**
   * Get the location field label including timestamp for the Asset History drawer.
   * Format: "Latest Location @ H:MMam, MM/DD/YY"
   * Falls back to "Latest Location" when location or timestamp is absent.
   */
  public getLocationTimestampLabel(latestEventTimestamp?: Date): string {
    if (!this.location) {
      return 'Latest Location';
    }
    const ts = latestEventTimestamp ?? this.locationUpdatedAt;
    if (!ts) {
      return 'Latest Location';
    }
    const dateStr = ts.toLocaleDateString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    });
    const timeStr = ts
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase()
      .replace(' ', '');
    return `Latest Location @ ${timeStr}, ${dateStr}`;
  }

  /**
   * Get location display value with timestamp
   * Format: "Location Name, MM/DD/YY, H:MMam/pm"
   * Returns 'N/A' if location is null
   */
  public getLocationDisplayWithTimestamp(): string {
    if (!this.location) {
      return 'N/A';
    }
    const locationDisplay = this.getLocationDisplay();
    if (!this.locationUpdatedAt) {
      return locationDisplay;
    }

    // Format date as MM/DD/YY
    const dateStr = this.locationUpdatedAt.toLocaleDateString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    });

    // Format time as H:MMam/pm (no leading zero for hours, lowercase am/pm)
    const timeStr = this.locationUpdatedAt
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase()
      .replace(' ', '');

    return `${locationDisplay}, ${dateStr}, ${timeStr}`;
  }

  /**
   * Get description display value
   * Returns empty string if description is null
   */
  public getDescriptionDisplay(): string {
    return this.description ?? '';
  }

  /**
   * Check if asset is available
   */
  public isAvailable(): boolean {
    return this.status === 'AVAILABLE';
  }

  /**
   * Returns the catalog name shared across physical instances of the same tray type.
   * Falls back to name for non-tray assets or when trayDetails is absent.
   */
  public get trayName(): string {
    return (
      (this.trayDetails as TrayDetails | null)?.countSheet?.trayName ??
      this.name
    );
  }

  /**
   * Returns the tray type for the asset.
   * Falls back to null for non-tray assets or when trayDetails is absent.
   */
  public get trayType(): TrayType | null {
    return (this.trayDetails as TrayDetails | null)?.trayType ?? null;
  }

  /**
   * Check if asset is a tray
   */
  public isTray(): boolean {
    return this.type === AssetType.TRAY;
  }

  /**
   * Convert back to DTO for API communication
   */
  public toDTO(): AssetResponseDto {
    return {
      id: this.id,
      name: this.name,
      description: this.description,

      type: this.type,
      status: this.status,
      manufacturer: this.manufacturer,
      referenceNumber: this.referenceNumber,
      createdAt: this.createdAt.toISOString(),
      assetCatalogId: this.assetCatalogId,
      locationUpdatedAt: this.locationUpdatedAt?.toISOString() ?? null,
      trayDetails: this.trayDetails as TrayDetails | null,
      location: this.location as AssetLocation | null,
      locationDisplayName: this.locationDisplayName,
      caseId: this.caseId,
    };
  }
}
